// logout.js
'use strict';
import { replace } from 'redux-first-history';

import { clearState } from '../resource/persist.js';
import { LOGOUT, PERSIST_NOW } from '../ActionTypes.js';
import { processMixpanelApiCall } from '../resource/mixpanel.js';
import { defaultIntercomSetting } from '../resource/intercomScript.js';
import getMeData from '../selector/getMeData.js';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';
import { sendMessageToSW } from '../serviceWorker/helpers.js';
import resetConfigurations from '../action/resetConfigurations.js';
import mergeMeData from '../action/mergeMeData.js';
import { setDatadogUserTag, clearDatadogUser } from '../resource/datadog.js';
import { setMixpanelViewIdMeData } from '../resource/getMixpanelViewId.js';

const hasCachesApi = typeof caches !== 'undefined';
const privateCacheKeys = ['message-resource', 'general-api'];

/**
 * Logout
 * @kind action
 * @param {string} [{pathname}='/'] - redirect path after logout
 * @param {string} [{search}='/'] - redirect search after logout
 * @param {boolean} [{isFromServiceWorker}=false] - if from service worker
 * @return {Promise} Action promise
 */
const logout =
  ({ pathname = '/', search = '', isFromServiceWorker = false } = {}) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    if (!token) {
      return dispatch({ type: '' });
    }

    // Delete FCM token
    const fcmToken = getMeData(getState(), 'fcmToken');
    if (fcmToken) {
      const { deleteFcmToken } = await import('../resource/fcm.js');
      try {
        await deleteFcmToken();
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }

    // Reset ServiceWorker dynamic caches.
    if (hasCachesApi) {
      caches
        .keys()
        .then(keys =>
          Promise.all(
            keys
              .filter(key => privateCacheKeys.includes(key))
              .map(key => caches.delete(key))
          )
        )
        .catch(() => null);
    }
    // for latter might use
    const sessionId = getMeData(getState(), 'sessionId');
    clearState();
    dispatch({ type: LOGOUT });

    dispatch(
      mergeMeData({ field: 'lastAgeVerifiedTimestamp', value: Date.now() })
    );

    setMixpanelViewIdMeData({
      meId: undefined,
      meUsername: undefined,
    });

    if (window.Intercom) {
      window.Intercom('shutdown');
      window.Intercom('boot', defaultIntercomSetting);

      const clientId = getMeData(getState(), 'clientId');
      window.Intercom('update', { client_id: clientId });
    }
    dispatch(resetConfigurations());

    if (!isFromServiceWorker) {
      // mixpanel reset and replace the device id to client id
      processMixpanelApiCall({ apiNames: ['reset'] });
      const clientId = getMeData(getState(), 'clientId');
      processMixpanelApiCall({
        apiNames: ['register'],
        args: [{ $device_id: clientId }],
      });
      clearDatadogUser();
      setDatadogUserTag('client_id', clientId);
      setDatadogUserTag('session_id', sessionId);

      await dispatch({ type: PERSIST_NOW });
      // only local logout action should post message to SW then clear event, channel.
      // if from SW means some tab logout action was called.
      sendMessageToSW({ type: MessageTypes.LOGOUT });
    }
    return dispatch(replace({ pathname, search }));
  };

export default logout;
